// Root render guard: without it, any render throw on a route that has no
// boundary of its own unmounts the whole tree to a blank white page (proven
// live by PlansPage's `tiers.map` throw). Sits above the router so a throw
// during routing is caught too. Error boundaries must be class components.
import * as Sentry from '@sentry/react';
import { Component, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SelfContainedOfflineStatusBanner } from '@/components/ap/status/OfflineStatusBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const AppErrorFallback = () => {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-ap-canvas p-8 text-foreground"
      data-testid="app-error-boundary"
    >
      {/* D4 (QA-29): the crash screen replaces the whole tree — connectivity
          must stay visible here, and OnlineStatusContext is gone with the
          tree, so this mount is the one self-contained instance. */}
      <SelfContainedOfflineStatusBanner className="fixed inset-x-0 top-0 z-50" />
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <CardTitle className="text-lg">{t('CommonText.something_went_wrong')}</CardTitle>
          <CardDescription>{t('AppErrorBoundary.reload_the_page_to_continue')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            data-testid="app-error-boundary-reload"
          >
            {t('CommonText.reload')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // captureReactException links the component stack as an error cause AND a
    // `react` context — higher fidelity than passing it as a plain `extra`.
    Sentry.captureReactException(error, errorInfo, {
      mechanism: { handled: true },
      captureContext: { tags: { surface: 'app-root' } },
    });
  }

  render() {
    if (this.state.hasError) {
      // i18n loads over the HTTP backend in suspense mode — a throw before the
      // locale lands must not make the fallback itself suspend-crash.
      return (
        <Suspense fallback={null}>
          <AppErrorFallback />
        </Suspense>
      );
    }
    return this.props.children;
  }
}
